import React, { useState } from 'react';
import { assignLocker } from '../data/firebaseService';
import Header from './Header';
import Footer from './Footer';
import VideoModal from './VideoModal';
import './DropOffForm.css';

const DropOffForm = ({ location, selectedLocker, onBack, onSuccess }) => {
  const [formData, setFormData] = useState({
    phoneNumber: '',
    serviceType: '',
    numberOfClothes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState('');
  const [showVideo, setShowVideo] = useState(false);

  const serviceTypes = [
    'Dryclean',
    'Wash & Fold @ ₹85-kg',
    'Wash & Steam Iron @ ₹120-kg',
    'Premium Laundry @ ₹170-kg',
    'Woolen Wash @ ₹140-kg'
  ];

  const locationNames = {
    'rajinder_nagar': 'Rajinder Nagar',
    'patel_nagar': 'Patel Nagar'
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    // Validate form
    if (!formData.phoneNumber || !formData.serviceType || !formData.numberOfClothes) {
      setError('Please fill in all fields');
      setIsSubmitting(false);
      return;
    }

    if (formData.phoneNumber.length < 10) {
      setError('Please enter a valid phone number');
      setIsSubmitting(false);
      return;
    }

    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const result = await assignLocker(location, selectedLocker, {
        phoneNumber: formData.phoneNumber,
        serviceType: formData.serviceType,
        numberOfClothes: parseInt(formData.numberOfClothes),
        dropOffTime: new Date().toISOString()
      });

      if (result) {
        setSuccess({
          lockerId: result.lockerId,
          pin: result.pin,
          customerData: formData
        });
      } else {
        setError('No available lockers at this location. Please try another location.');
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="dropoff-success">
        <Header />
        <div className="success-container">
          <h2>✅ Drop-Off Successful!</h2>
          <div className="success-details">
            <p><strong>Location:</strong> {locationNames[location]}</p>
            <p><strong>Locker:</strong> {success.lockerId.toUpperCase()}</p>
            <p><strong>Your PIN:</strong> <span className="pin">{success.pin}</span></p>
            <p><strong>Service:</strong> {success.customerData.serviceType}</p>
            <p><strong>Clothes:</strong> {success.customerData.numberOfClothes} items</p>
          </div>
          <div className="success-instructions">
            <h3>📋 Instructions:</h3>
            <ul>
              <li><strong>ENTER PASSCODE</strong></li>
              <li><strong>PRESS OK AND TURN KNOB TO RIGHT</strong></li>
              <li><strong>Drop/ COLLECT YOUR GARMENTS</strong></li>
              <li><strong>AND TURN KNOB TO TO THE LEFT AND CHECK IF THE LOCKER IS LOCKED</strong></li>
            </ul>
          </div>
          
          <div className="success-actions">
            <button 
              className="video-btn" 
              onClick={() => setShowVideo(true)}
            >
              🎥 Watch Instruction Video
            </button>
            
            <button className="back-btn" onClick={onBack}>
              🏠 Back to Home
            </button>
          </div>
        </div>
        <Footer />
        
        <VideoModal
          isOpen={showVideo}
          onClose={() => setShowVideo(false)}
          videoSrc="/instruction-video.mp4"
          title="How to Use Your PIN Code"
        />
      </div>
    );
  }

  return (
    <div className="dropoff-form">
      <Header />
      <div className="form-container">
        <h1 className="page-title">Drop Off Clothes</h1>
        <div className="form-header">
          <button className="back-btn" onClick={onBack}>
            ← Back
          </button>

          <p className="location-info">📍 {locationNames[location]}</p>
        </div>

        <form onSubmit={handleSubmit} className="form">
          <div className="form-group">
            <label htmlFor="phoneNumber">📱 Mobile Number</label>
            <input
              type="tel"
              id="phoneNumber"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleInputChange}
              placeholder="Enter your mobile number"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="serviceType">🛠️ Service Type</label>
            <select
              id="serviceType"
              name="serviceType"
              value={formData.serviceType}
              onChange={handleInputChange}
              required
            >
              <option value="">Select a service</option>
              {serviceTypes.map((service) => (
                <option key={service} value={service}>
                  {service}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="numberOfClothes">👕 Number of Clothes</label>
            <input
              type="number"
              id="numberOfClothes"
              name="numberOfClothes"
              value={formData.numberOfClothes}
              onChange={handleInputChange}
              placeholder="Enter number of items"
              min="1"
              max="50"
              required
            />
          </div>

          {error && (
            <div className="error-message">
              ⚠️ {error}
            </div>
          )}

          <button
            type="submit"
            className="submit-btn"
            disabled={isSubmitting}
          >
            {isSubmitting ? '🔄 Processing...' : '📦 Drop Off Clothes'}
          </button>
        </form>
      </div>
      <Footer />
    </div>
  );
};

export default DropOffForm; 